import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Tag } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page, InstallPromotionCommissionVo } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { INSTALL_PROMOTION_COMMISSION_STATE } from "@/meta/expenseManage/installPromotionCommission";

type CommissionRow = Partial<InstallPromotionCommissionVo>;
type CommissionPage = Page<CommissionRow>;

export interface InstallPromotionCommissionQuery {
    page: number;
    size: number;
    installPromotionRecordCode: string;
    isAsc?: number;
}

export interface InstallPromotionCommissionListProps {
    code?: string;
    loadCommissions?: (query: InstallPromotionCommissionQuery) => Promise<CommissionPage>;
}

/** 复用 Vue 安装推广佣金明细分页接口。 */
export async function loadInstallPromotionCommissions(query: InstallPromotionCommissionQuery): Promise<CommissionPage> {
    const response = await request.service.get<CommissionPage>("/install-promotion-commission/paging", {
        params: query
    });
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 安装推广记录详情中的佣金明细，保留 Vue 表格字段和导航行为。 */
export function InstallPromotionCommissionList({
    code,
    loadCommissions = loadInstallPromotionCommissions
}: InstallPromotionCommissionListProps) {
    const [query, setQuery] = useState<InstallPromotionCommissionQuery>({
        page: 1,
        size: 10,
        installPromotionRecordCode: code || ""
    });
    const [pageData, setPageData] = useState<CommissionPage>({
        list: [],
        pageSize: 10,
        currentPage: 1,
        totalPage: 0,
        total: 0
    });
    const [errorMessage, setErrorMessage] = useState<string | null>(code ? null : "编号不存在");

    useEffect(() => {
        if (!code) {
            setErrorMessage("编号不存在");
            setPageData(current => ({ ...current, list: [], total: 0 }));
            return;
        }

        // 详情页记录编号变化时重置到第一页，避免沿用上一条记录的分页位置。
        const nextQuery = { ...query, installPromotionRecordCode: code };
        if (query.installPromotionRecordCode !== code) setQuery(nextQuery);
        let disposed = false;
        void loadCommissions(nextQuery)
            .then(result => {
                if (disposed) return;
                setPageData(result);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setPageData(current => ({ ...current, list: [], total: 0 }));
                setErrorMessage("安装推广佣金明细加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [code, loadCommissions, query]);

    // 保留 Vue ProTable 创建时间排序的 isAsc 查询参数。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) delete nextQuery.isAsc;
                else nextQuery.isAsc = order === "ascending" ? 1 : 0;
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 佣金明细 ProTable 字段和导航插槽。
    const columns = useMemo<TableColumnProps<CommissionRow>[]>(
        () => [
            {
                key: "id",
                label: "编号",
                minWidth: 120,
                prop: "commission.id",
                render: ({ row }) =>
                    row.commission?.id == null ? (
                        DEFAULT_VALUE
                    ) : (
                        <Link to={`/expenseManagement/installPromotion/detail/${row.commission.id}`}>
                            {row.commission.id}
                        </Link>
                    )
            },
            {
                key: "orderShipId",
                label: "船运单",
                minWidth: 148,
                prop: "commission.orderShipId",
                // 沿用 Vue 船运单详情地址，避免安装推广记录跳转到不存在的旧路径。
                render: ({ row }) =>
                    row.commission?.orderShipId == null ? (
                        DEFAULT_VALUE
                    ) : (
                        <Link to={`/logistics/shippingNeed/waybillManagementInfo/${row.commission.orderShipId}`}>
                            {row.commission.orderShipId}
                        </Link>
                    )
            },
            {
                key: "commissionAmount",
                label: "结佣金额（￥）",
                minWidth: 148,
                prop: "commission.commissionAmount",
                render: ({ row }) => row.commission?.commissionAmount ?? DEFAULT_VALUE
            },
            {
                key: "state",
                label: "状态",
                width: 100,
                prop: "state",
                render: ({ row }) => (
                    <Tag
                        color={
                            row.commission?.state === INSTALL_PROMOTION_COMMISSION_STATE.SETTLED.code
                                ? "default"
                                : undefined
                        }
                    >
                        {INSTALL_PROMOTION_COMMISSION_STATE.getIntroByCode(row.commission?.state) || DEFAULT_VALUE}
                    </Tag>
                )
            },
            {
                key: "createdTime",
                label: "创建时间",
                minWidth: 154,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => formatDate(row.commission?.createdTime)
            }
        ],
        []
    );

    return (
        <section aria-label="install-promotion-commission-list">
            <ZhTable<CommissionRow>
                rowKey={row => String(row.commission?.id || row.virtualId || "commission")}
                data={pageData.list || []}
                columns={columns}
                pagination={false}
                onSortChange={handleSortChange}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination
                            current={query.page}
                            pageSize={query.size}
                            total={pageData.total || 0}
                            hideOnSinglePage
                            onChange={(page, pageSize) =>
                                setQuery(current => ({
                                    ...current,
                                    // 切换每页条数后回到第一页，普通翻页保留当前页。
                                    page: pageSize !== current.size ? 1 : page,
                                    size: pageSize
                                }))
                            }
                        />
                    </div>
                }
            />
        </section>
    );
}
